/**
 * Plan wählen und Credits nachkaufen, auf der Kontoseite (seit 27.09.2026).
 *
 * Entscheidung Robert: Selbstnutzer zahlen einen Monatsplan und können Pakete
 * nachkaufen. Dieselben Zahlen zeigt die Pricing-Seite der Website.
 *
 * NICHT BUCHBAR, ABER SICHTBAR: Solange ein Plan nicht kaufbar ist (`buchbar:
 * false`: nicht aktiv, oder Mollie läuft mit Testschlüssel und dies ist kein
 * Testkonto), steht die Karte mit Preis und „Bald verfügbar" da, ohne Knopf
 * zum Bezahlen. Ausblenden hätte die App stumm gemacht, während die Website
 * dieselben Preise nennt.
 *
 * Wechseln und Kündigen stehen hier, seit 07.10.2026 über Mollie: Mollie hat
 * kein Kundenportal wie Stripe. Ein Wechsel gilt ab der nächsten Abbuchung,
 * eine Kündigung beendet das Abo, das bezahlte Kontingent bleibt bis zum
 * Periodenende.
 *
 * Pläne nach Kundentyp (seit 09.10.2026): jede Karte nennt, wie viele eigene
 * Zielgruppen der Plan erlaubt und ob Berichte nur für eingeladene Adressen
 * gehen. Die Werte kommen mit den Plänen aus der billing-Function, was ohne
 * Plan gilt aus `status.ohne_plan`.
 *
 * Seit 16.10.2026 (#270, #271, #273): Preise brutto als Hauptpreis, netto
 * daneben für Unternehmen. „Plan wählen“ und „Kaufen“ öffnen erst die
 * Bestellübersicht (`Bestellung.tsx`), bezahlt wird erst nach
 * „Zahlungspflichtig bestellen“. „Kündigen“ heißt „Verträge hier kündigen“
 * und führt auf die Seite ohne Anmeldung (`/kuendigen`), statt eines
 * Bestätigungsdialogs im Browser.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ApiFehler, starteKarte, wechslePlan, type Paket, type Plan, type Status } from '../api';
import Bestellung, { type Auswahl } from './Bestellung';
import { ABGLEICH, AUSBAU, centJeCredit, centJeCreditAus, CHALLENGER, preisAnzeige, SATZ_DE, EINRICHTUNG, gespraecheZeile, leserZeile, planAktion, reichweiteSatz, STANDARD, VERGLEICH_1ZU1, VERGLEICH_STANDARD, VORAUSWAHL, zielgruppenZeile, type PlanAktion } from '../preise';
import { Abschnitt, Hinweis, KleinerKnopf, Kreisel, Marke, Panel } from '../ui';
import { locale, t } from '../sprache';

const zahl = (n: number) => new Intl.NumberFormat(locale()).format(n);
const tag = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(locale(), { day: '2-digit', month: 'long', year: 'numeric' }) : '';

const KNOPF = (): Record<PlanAktion, string> => ({
  verwalten: t('Verträge hier kündigen', 'Cancel contracts here'),
  wechseln: t('Wechseln', 'Switch'),
  waehlen: t('Plan wählen', 'Choose plan'),
  bald: t('Bald verfügbar', 'Coming soon'),
});

export default function PlaeneUndPakete({ status, eurInUsd = false, neuLaden }: { status: Status; eurInUsd?: boolean; neuLaden?: () => void }) {
  const [arbeitet, setArbeitet] = useState('');
  const [fehler, setFehler] = useState('');
  const [erledigt, setErledigt] = useState('');
  const [auswahl, setAuswahl] = useState<Auswahl | null>(null);
  const navigate = useNavigate();
  const satz = status.steuer?.satz ?? SATZ_DE;

  function waehle(a: Auswahl) {
    setFehler('');
    setErledigt('');
    setAuswahl(a);
    requestAnimationFrame(() => document.getElementById('bestellung')?.scrollIntoView({ block: 'start', behavior: 'smooth' }));
  }

  function zeigeFehler(e: unknown) {
    const f = e as ApiFehler;
    setFehler(f.message);
    // Ohne Rechnungsdaten kein Kauf: dorthin, wo man sie einträgt.
    if (f.kennung === 'rechnungsdaten_fehlen') document.getElementById('rechnungsdaten')?.scrollIntoView({ block: 'start' });
  }

  /** Zu Mollie wechseln. Die Seite verlässt die App; zurück kommt man über /account?checkout=… */
  async function weiter(schluessel: string, holen: () => Promise<{ url: string }>) {
    setArbeitet(schluessel);
    setFehler('');
    setErledigt('');
    try {
      const { url } = await holen();
      window.location.assign(url);
    } catch (e) {
      zeigeFehler(e);
      setArbeitet('');
    }
  }

  /** Wechseln oder Kündigen: bleibt in der App, danach den Stand neu laden. */
  async function aendern(schluessel: string, tun: () => Promise<string>) {
    setArbeitet(schluessel);
    setFehler('');
    setErledigt('');
    try {
      setErledigt(await tun());
      neuLaden?.();
    } catch (e) {
      zeigeFehler(e);
    } finally {
      setArbeitet('');
    }
  }

  function planLos(p: Plan, aktion: PlanAktion) {
    if (aktion === 'waehlen') return waehle({ art: 'plan', plan: p });
    if (aktion === 'wechseln') {
      return void aendern(p.plan, async () => {
        const r = await wechslePlan(p.plan);
        return r.ab_naechster_zahlung
          ? t(`Gemerkt: ab der nächsten Abbuchung${r.naechste_zahlung ? ` am ${tag(r.naechste_zahlung)}` : ''} gilt ${p.name}. Bis dahin bleibt alles, wie es ist.`,
            `Noted: from the next charge${r.naechste_zahlung ? ` on ${tag(r.naechste_zahlung)}` : ''}, ${p.name} applies. Until then, nothing changes.`)
          : t('Der Wechsel ist aufgehoben, dein Plan bleibt.', 'The switch is canceled, your plan stays.');
      });
    }
    // Kündigen über die Seite nach § 312k BGB, dieselbe wie ohne Anmeldung.
    if (aktion === 'verwalten') return void navigate('/kuendigen');
  }

  const plaene = status.plans;
  const pakete: Paket[] = status.packs ?? [];
  const karte = status.karte?.angeboten ? status.karte : null;
  if (!plaene.length && !pakete.length && !karte) return null;

  return (
    <>
      {fehler && <Hinweis art="schlecht">{fehler}</Hinweis>}
      {erledigt && <Hinweis art="gut">{erledigt}</Hinweis>}
      {auswahl && (
        <div id="bestellung" className="scroll-mt-4">
          <Bestellung auswahl={auswahl} status={status} abbrechen={() => setAuswahl(null)} />
        </div>
      )}

      {/* Karte gegen Credits (#155), Vorbild Firecrawl. Steht vor den Plänen,
          weil es das Einzige hier ist, das nichts kostet. Kein Abo danach:
          kaufen ist immer ein eigener Klick (§ 312j BGB). */}
      {karte && (
        <div id="karte" className="mb-10 scroll-mt-4">
          <Abschnitt
            titel={t(`${zahl(karte.credits)} Credits für eine hinterlegte Karte`, `${zahl(karte.credits)} credits for adding a card`)}
            unter={t('Einmal je Konto. Abgebucht wird nichts, und es entsteht kein Abo. Credits kaufst du erst, wenn du selbst auf Kaufen klickst.',
              'Once per account. Nothing is charged, and no subscription starts. You only buy credits when you click Buy yourself.')}
          >
            <Panel>
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5 px-4 py-3.5 sm:px-5">
                <div className="min-w-0 text-[0.9375rem] text-text">
                  {t(`Karte bei Mollie hinterlegen, die ${zahl(karte.credits)} Credits kommen sofort dazu.`,
                    `Add a card at Mollie, the ${zahl(karte.credits)} credits are added right away.`)}
                  <div className="mt-0.5 text-[0.8125rem] text-text-hell">{reichweiteSatz(karte.credits)}</div>
                </div>
                <KleinerKnopf
                  type="button" disabled={!!arbeitet}
                  className="!border-knopf !bg-knopf !text-auf-marke hover:!bg-knopf-hover"
                  onClick={() => void weiter('karte', starteKarte)}
                >
                  {arbeitet === 'karte' ? <><Kreisel className="mr-1.5 size-3" />{t('Einen Moment …', 'One moment …')}</> : t('Karte hinterlegen', 'Add card')}
                </KleinerKnopf>
              </div>
            </Panel>
          </Abschnitt>
        </div>
      )}

      {plaene.length > 0 && (
        <Abschnitt titel="Plan" unter={<>
          {t('Monatlich kündbar. Ein Wechsel gilt ab der nächsten Abbuchung.', 'Cancel monthly. A switch applies from the next charge.')} {Number(satz) > 0
            ? t(`Preise inkl. ${satz.replace(/\.0+$/, '').replace('.', ',')} % Umsatzsteuer, für Unternehmen steht der Nettopreis daneben.`,
              `Prices incl. ${satz.replace(/\.0+$/, '')}% VAT, the net price for businesses is shown next to it.`)
            : t('Preise ohne Umsatzsteuer.', 'Prices without VAT.')}
          {status.ohne_plan && <> {t('Ohne Plan, auch nur mit Paketen:', 'Without a plan, also with packs only:')} {[zielgruppenZeile(status.ohne_plan.max_eigene_zielgruppen), leserZeile(status.ohne_plan.leserliste)].filter(Boolean).join(', ')}.</>}
        </>}>
          <ul className="m-0 grid list-none gap-3 p-0 md:grid-cols-3">
            {plaene.map(p => (
              <PlanKarte
                key={p.plan} p={p} status={status} satz={satz} arbeitet={arbeitet === p.plan}
                gesperrt={!!arbeitet}
                los={aktion => planLos(p, aktion)}
              />
            ))}
          </ul>
        </Abschnitt>
      )}

      {pakete.length > 0 && (
        <div id="pakete" className="mt-10 scroll-mt-4">
          <Abschnitt
            titel={t('Credits nachkaufen', 'Buy more credits')}
            unter={t('Einmal bezahlt, ohne Abo, und gekaufte Credits verfallen nicht. Verbraucht wird zuerst das Monatskontingent.',
              'Paid once, no subscription, and purchased credits do not expire. The monthly allowance is used first.')}
          >
            <Panel>
              <ul className="m-0 list-none p-0">
                {pakete.map(k => { const preis = k.preis_netto_cent != null ? preisAnzeige(k.preis_netto_cent, satz) : null; return (
                  <li key={k.pack} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5 border-t border-linie-zart px-4 py-3.5 first:border-t-0 sm:px-5">
                    <div className="min-w-0">
                      <span className="text-[0.9375rem] font-semibold text-tinte">{k.name}</span>
                      <span className="text-[0.8125rem] text-text-hell tabular-nums">
                        {' · '}{preis ? preis.haupt : k.price_label}
                        {(preis ? centJeCreditAus(preis.cent, k.credits) : centJeCredit(k.price_label, k.credits))
                          ? <> · {preis ? centJeCreditAus(preis.cent, k.credits) : centJeCredit(k.price_label, k.credits)} {t('ct je Credit', 'ct per credit')}</> : null}
                      </span>
                      {preis && <div className="mt-0.5 text-[0.75rem] text-text-hell tabular-nums">{preis.zusatz}</div>}
                      <div className="mt-0.5 text-[0.8125rem] text-text-hell">{reichweiteSatz(k.credits)}</div>
                    </div>
                    {k.buchbar ? (
                      <KleinerKnopf
                        type="button" disabled={!!arbeitet}
                        onClick={() => waehle({ art: 'paket', paket: k })}
                      >
                        {t('Kaufen', 'Buy')}
                      </KleinerKnopf>
                    ) : (
                      <span className="text-[0.8125rem] font-medium text-text-hell">{t('Bald verfügbar', 'Coming soon')}</span>
                    )}
                  </li>
                ); })}
              </ul>
            </Panel>
          </Abschnitt>
        </div>
      )}

      <div className="mt-10">
        {/* Die Preisregel stand bis zum 03.10.2026 als Absatz unter dem
            Guthaben, wo sie das Guthaben vom Plan trennte. Hier steht sie
            neben den Beispielen, aus denen sie folgt. */}
        <Abschnitt
          titel={t('Was ein Credit kauft', 'What a credit buys')}
          unter={t(<>
            Ein Einzelurteil (eine Persona liest einen Text und urteilt) kostet 1,5 Credits, bei langen Seiten etwas mehr.
            Ein Vergleich zweier Texte umfasst höchstens {STANDARD.simRuns} Einzelurteile, und was mit gleicher Eingabe schon einmal lief, kostet nichts.
            {eurInUsd && ' Für die Modellkosten ist kein Euro-Kurs hinterlegt, die Beträge stehen in USD.'}
          </>, <>
            A single verdict (one persona reads a text and judges it) costs 1.5 credits, a bit more for long pages.
            A comparison of two texts takes at most {STANDARD.simRuns} single verdicts, and anything that already ran with the same input costs nothing.
            {eurInUsd && ' No euro exchange rate is set for model costs, the amounts are in USD.'}
          </>)}
        >
          <Panel>
            <dl className="m-0 text-[0.9375rem]">
              {(() => { const bis = t('bis', 'to'); return [
                [t('Neue Zielgruppe einrichten, einmalig', 'Set up a new audience, one-off'), `${EINRICHTUNG}`],
                [t(`Vergleich zweier Texte, ${STANDARD.personas} Personas × ${STANDARD.jobs} Jobs, Vergleichsfassung erzeugt`,
                  `Comparison of two texts, ${STANDARD.personas} personas × ${STANDARD.jobs} jobs, baseline generated`), `${VERGLEICH_STANDARD}`],
                [t(`Derselbe Vergleich, jede Persona nur mit ihrem eigenen Job (${STANDARD.personas} Einzelurteile)`,
                  `The same comparison, each persona only with its own job (${STANDARD.personas} single verdicts)`), `${VERGLEICH_1ZU1}`],
                [t(`Seite Schritt für Schritt ausbauen, ${STANDARD.personas} Personas`, `Build a page step by step, ${STANDARD.personas} personas`), `${AUSBAU.von} ${bis} ${AUSBAU.bis}`],
                [t('Kernaussagen gegen Wettbewerb prüfen', 'Check key claims against competitors'), `${ABGLEICH.von} ${bis} ${ABGLEICH.bis}`],
                [t('Entwürfe vorsortieren', 'Pre-sort drafts'), `${VORAUSWAHL.von} ${bis} ${VORAUSWAHL.bis}`],
                [t('Challenger-Fassungen schreiben lassen', 'Have challenger versions written'), `${CHALLENGER.von} ${bis} ${CHALLENGER.bis}`],
              ] as const; })().map(([was, credits]) => (
                <div key={was} className="flex items-baseline justify-between gap-4 border-t border-linie-zart px-4 py-2.5 first:border-t-0 sm:px-5">
                  <dt className="min-w-0 text-text">{was}</dt>
                  <dd className="m-0 shrink-0 font-semibold whitespace-nowrap text-tinte tabular-nums">{credits}</dd>
                </div>
              ))}
            </dl>
          </Panel>
        </Abschnitt>
      </div>
    </>
  );
}

function PlanKarte({ p, status, satz, arbeitet, gesperrt, los }: {
  p: Plan; status: Status; satz: string; arbeitet: boolean; gesperrt: boolean; los: (aktion: PlanAktion) => void;
}) {
  const aktion = planAktion(p, status);
  const aktuell = aktion === 'verwalten';
  const preis = p.preis_netto_cent != null ? preisAnzeige(p.preis_netto_cent, satz) : null;
  const [betrag, einheit] = preis ? [preis.haupt, t('Monat', 'month')] : p.price_label.split('/').map(x => x.trim());
  const merkmale = [zielgruppenZeile(p.max_eigene_zielgruppen), leserZeile(p.leserliste), gespraecheZeile(p.eigene_gespraeche)].filter((z): z is string => !!z);
  return (
    <li className={`flex flex-col rounded-karte border bg-flaeche p-4 shadow-flaeche sm:p-5 ${
      aktuell ? 'border-marke/50 ring-1 ring-marke/40' : 'border-linie'}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[0.9375rem] font-semibold text-tinte">{p.name}</span>
        {aktuell && <Marke art="marke">{t('Dein Plan', 'Your plan')}</Marke>}
      </div>
      <div className="mt-2 font-serif text-[1.75rem] leading-none font-semibold tracking-[-0.015em] text-tinte tabular-nums">
        {betrag}{einheit && <span className="ml-1 font-sans text-[0.8125rem] font-medium tracking-normal text-text-leise">/ {einheit}</span>}
      </div>
      {preis && <div className="mt-1 text-[0.75rem] text-text-hell tabular-nums">{preis.zusatz}</div>}
      <div className="mt-2.5 text-[0.9375rem] text-text">{t(`${zahl(p.monthly_credits)} Credits im Monat`, `${zahl(p.monthly_credits)} credits per month`)}</div>
      <div className="mt-1 text-[0.8125rem] leading-snug text-text-hell">{reichweiteSatz(p.monthly_credits)}</div>
      {merkmale.length > 0 && (
        <ul className="m-0 mt-3 flex list-none flex-col gap-1 border-t border-linie-zart p-0 pt-3 text-[0.8125rem] text-text">
          {merkmale.map(m => <li key={m}>{m}</li>)}
        </ul>
      )}
      {aktuell && status.subscription?.current_period_end && (
        <div className="mt-1 text-[0.8125rem] text-text-hell">{t(`Nächste Abbuchung am ${tag(status.subscription.current_period_end)}`, `Next charge on ${tag(status.subscription.current_period_end)}`)}</div>
      )}
      {status.subscription?.status === 'canceled' && status.subscription.plan === p.plan && status.subscription.current_period_end
        && new Date(status.subscription.current_period_end).getTime() > Date.now() && (
        <div className="mt-1 text-[0.8125rem] text-text-hell">{t(`Gekündigt, die Credits gelten bis ${tag(status.subscription.current_period_end)}`, `Canceled, the credits are valid until ${tag(status.subscription.current_period_end)}`)}</div>
      )}
      {status.subscription?.ab_naechster_zahlung === p.plan && (
        <div className="mt-1 text-[0.8125rem] font-medium text-marke-tief">{t('Gilt ab der nächsten Abbuchung', 'Applies from the next charge')}</div>
      )}
      <div className="mt-auto pt-4">
        {aktion === 'bald' ? (
          <span className="flex h-[2.5rem] items-center justify-center rounded-lg border border-linie bg-ebene text-[0.875rem] font-medium text-text-hell">
            {t('Bald verfügbar', 'Coming soon')}
          </span>
        ) : (
          <button
            type="button" disabled={gesperrt} onClick={() => los(aktion)}
            className={`h-[2.5rem] w-full cursor-pointer rounded-lg text-[0.875rem] font-semibold transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-60 ${
              aktion === 'waehlen'
                ? 'border-0 bg-knopf text-auf-marke hover:bg-knopf-hover'
                : 'border border-linie bg-flaeche text-text hover:border-linie-stark hover:bg-ebene'}`}
          >
            {arbeitet ? <span className="inline-flex items-center gap-2"><Kreisel className="size-3.5" />{t('Einen Moment …', 'One moment …')}</span> : KNOPF()[aktion]}
          </button>
        )}
      </div>
    </li>
  );
}
